import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { useForm } from '@tanstack/react-form'
import { useLocation } from '@tanstack/react-router'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'

import { RichTextEditor } from '@/components/shared/rich-text-editor'
import { noteMentionedUserIds, useNoteMentionExtension } from './note-mentions'

import { CreateNoteRequestSchema, UpdateNoteRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { useCreateNote, useUpdateNote } from '@/stores/notes.store'
import type { Note } from '@/gen/cortado/v1/actions_pb'

import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet'
import type { SheetLayout } from '@/components/shared/sheet-layout'
import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'

interface NoteSheetProps {
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  note?: Note // If provided, this is edit mode; otherwise create mode
  /** Called after a successful create or update, before the sheet closes. */
  onSaved?: () => void
  open: boolean
  onOpenChange: (open: boolean) => void
  /** 'pipeline' applies the shared pipeline sheet width and footer layout. */
  layout?: SheetLayout
}

/**
 * NoteSheet - Sheet component for creating notes with rich text editor
 *
 * Features:
 * - Rich text editing with TipTap
 * - Toolbar with formatting buttons (bold, italic, bullet list, numbered list)
 * - Validates note content is not empty
 * - Uses TanStack Form for form state management
 * - Leverages useCreateNote hook for optimized cache updates
 * - Shows loading states during mutation
 * - Shows toast notifications on success/error
 * - Resets form on close
 *
 * @param organizationId - Organization ID for the note
 * @param parentType - Type of parent entity (inquiry, contact, booking, event)
 * @param parentId - ID of the parent entity
 * @param open - Control sheet visibility
 * @param onOpenChange - Handle open/close state changes
 */
export function NoteSheet({
  organizationId,
  parentType,
  parentId,
  note,
  onSaved,
  open,
  onOpenChange,
  layout,
}: NoteSheetProps) {
  const isEditMode = !!note
  const { mutation: createMutation } = useCreateNote()
  const { mutation: updateMutation } = useUpdateNote()
  const location = useLocation()
  const mentionExtension = useNoteMentionExtension()

  // Draft persistence refs - preserve content across sheet open/close until route changes
  const createDraftRef = React.useRef<string>('')
  const editDraftsRef = React.useRef<Map<string, string>>(new Map())
  const lastRouteRef = React.useRef<string>(location.pathname)
  // Track which note we're editing (parent may clear note prop before close effect runs)
  const currentNoteIdRef = React.useRef<string | undefined>(undefined)

  // Setup TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Placeholder.configure({
        placeholder: 'Write your note here... Use @ to mention someone',
      }),
      mentionExtension,
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[200px] px-3 py-2',
      },
    },
  })

  // Clear drafts when route changes
  React.useEffect(() => {
    if (location.pathname !== lastRouteRef.current) {
      createDraftRef.current = ''
      editDraftsRef.current.clear()
      lastRouteRef.current = location.pathname
    }
  }, [location.pathname])

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      note: '',
    },
    onSubmit: async ({ value: _value }) => {
      try {
        // Get HTML content from editor
        const htmlContent = editor?.getHTML() || ''

        // Validate note content is not empty (strip HTML tags for validation)
        const textContent = editor?.getText().trim() || ''
        if (!textContent) {
          toast.error('Note content is required')
          return
        }

        const uniqueMentionedUserIds = editor ? noteMentionedUserIds(editor.state.doc) : []

        if (isEditMode && note) {
          // Update existing note
          const request = create(UpdateNoteRequestSchema, {
            id: note.id,
            note: htmlContent,
            mentionedUserIds: uniqueMentionedUserIds,
            updateMask: create(FieldMaskSchema, {
              paths: ['note'],
            }),
          })

          await updateMutation.mutateAsync(request)
          // Clear draft for this note since it's now saved
          editDraftsRef.current.delete(note.id)
          toast.success('Note updated successfully')
        } else {
          // Create note request
          const request = create(CreateNoteRequestSchema, {
            organizationId,
            parentType,
            parentId,
            note: htmlContent,
            mentionedUserIds: uniqueMentionedUserIds,
          })

          await createMutation.mutateAsync(request)
          // Clear create draft since it's now saved
          createDraftRef.current = ''
          toast.success('Note created successfully')
        }

        // Clear tracking ref so close effect doesn't re-save as draft
        currentNoteIdRef.current = undefined
        // Clear editor before closing to prevent draft save
        editor?.commands.clearContent()
        form.reset()
        onSaved?.()
        onOpenChange(false)
      } catch (error) {
        console.error('Failed to save note:', error)
        reportError(error, { feature: 'note_save', kind: 'mutation' })
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'note-sheet' })
        toast.error(`Failed to ${isEditMode ? 'update' : 'create'} note`, {
          description: getErrorDescription(error),
        })
      }
    },
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  // Update form when editor content changes
  React.useEffect(() => {
    if (editor) {
      const handleUpdate = () => {
        form.setFieldValue('note', editor.getHTML())
      }
      editor.on('update', handleUpdate)
      return () => {
        editor.off('update', handleUpdate)
      }
    }
  }, [editor, form])

  // Set editor content when sheet opens - restore from draft if available
  React.useEffect(() => {
    if (editor && open) {
      // Track which note we're editing for draft saving on close
      currentNoteIdRef.current = note?.id

      if (isEditMode && note) {
        // Edit mode: check for draft first, fall back to original note content
        const draft = editDraftsRef.current.get(note.id)
        editor.commands.setContent(draft ?? note.note ?? '')
      } else {
        // Create mode: restore draft if exists
        editor.commands.setContent(createDraftRef.current || '')
      }
    }
  }, [editor, open, isEditMode, note])

  // Save draft and reset editor when sheet closes
  React.useEffect(() => {
    if (!open && editor) {
      // Save current content to draft before clearing
      const content = editor.getHTML()
      const noteId = currentNoteIdRef.current

      if (noteId) {
        // Was editing an existing note
        editDraftsRef.current.set(noteId, content)
      } else {
        // Was creating a new note
        createDraftRef.current = content
      }

      // Clear editor (draft is preserved in ref)
      editor.commands.clearContent()
      form.reset()
    }
  }, [open, editor, form])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn(layout === 'pipeline' && 'w-full sm:max-w-xl')}>
        <SheetHeader>
          <SheetTitle>{isEditMode ? 'Edit Note' : 'New Note'}</SheetTitle>
          <SheetDescription>
            {isEditMode
              ? 'Update the note. Use the toolbar to format your text. Click save when you\'re done.'
              : 'Create a new note. Use the toolbar to format your text. Click save when you\'re done.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="note-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
          <form.Field name="note">
            {(_field) => (
              <div className="grid">
                <Label htmlFor="note" className="mb-3">
                  Note <span className="text-destructive">*</span>
                </Label>

                <RichTextEditor
                  editor={editor}
                  disabled={isPending}
                  minHeight="200px"
                  showMentionButton
                />
              </div>
            )}
          </form.Field>

          </form>
        </SheetBody>
        <SheetFooter className={cn(layout === 'pipeline' && 'sm:justify-between')}>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </SheetClose>
          <form.Subscribe selector={(s) => [s.values, s.isSubmitting] as const}>
            {([_values, isSubmitting]) => {
              const textContent = editor?.getText().trim() ?? ''
              const disabled = isPending || isSubmitting || !textContent
              return (
                <Button type="submit" form="note-form" disabled={disabled}>
                  {isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              )
            }}
          </form.Subscribe>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

